import _                                                    from 'underscore';
import React, { useEffect, useRef, useState }               from 'react';
import { Dropdown, Form, Icon, Menu, Popup, SemanticICONS } from 'semantic-ui-react';
import { Formik, FormikProps }                              from 'formik';
import * as Yup                                             from 'yup';
import { v4 as uuid }                                       from 'uuid';
import moment                                               from "moment";
import { useQuery }                                         from '@apollo/react-hooks';

import { objectEquivalenceDiff, runIfFunction, trimString }                                                                                        from '@src/util';
import { Align, CorvalColor, CorvalMilestone, CorvalProject, CorvalProjectVersionMilestone, CorvalUIColor, Position, SemanticUIColor, Size, Uuid } from '@src/types';
import { queriesHaveData, queriesLoading }                                                                                                         from '@src/store/_apolloUtil';
import { QueryDialogProps, QueryOpDialog }                                                                                                         from '../../../components/from-query/QueryOpDialog';
import { BasicDialogContainer }                                                                                                                    from '../../../components/modals/BasicDialogContainer';
import { StyledContainer }                                                                                                                         from '../../../containers';
import { LoadingIndicator }                                                                                                                        from '../../../components/util/loading/LoadingIndicator';
import { Cell, Grid }                                                                                                                              from "styled-css-grid";
import { ENTITY_COLORIZATION, ENTITY_ICONS, PROJECT_ENTITY_SUPPORTED_ICON_NAMES }                                                                  from "@src/constants";
import { ProjectQueries, StoreQueries }                                                                                                            from "@src/queries";
import { DateField, FieldWrapper, MultipleInputArrayField, TextField }                                                                             from '@src/views/components/forms';
import { ColorMenu }                                                                                                                               from "@src/views/components/menus/ColorMenu";
import { IconMenu }                                                                                                                                from "@src/views/components/menus/IconMenu";
import { addStringMinCharDescriptorTest, useFeatureFlags, useYupExtensions }                                                                       from "@src/hooks";
import { TimeService }                                                                                                                             from "@src/services";
import { FieldLabel }                                                                                                                              from "@src/views/components/forms/formik/FieldLabel";
import { AllProjectVersionMilestonesDropdown }                                                                                                     from "@src/views/components/forms/dropdowns/AllProjectVersionMilestonesDropdown";
import { MenuItemWithIconAndIndicator }                                                                                                            from "@src/views/components/menus/MenuItemWithIconAndIndicator";
import { UseIf }                                                                                                                                   from "@src/views/components/display/EqualValues";
import { FinePrintMessage }                                                                                                                        from "@src/views/components/util/messaging/FinePrintMessage";
import { Tooltip }                                                                                                                                 from "@src/views/components/util/messaging/ToolTip";
import { TextLink }                                                                                                                                from "@src/views/components/display/TextLink";


const supportedProjectVersionMilestoneColorNames: CorvalColor[] = [];
const supportedProjectVersionMilestoneIconNames: SemanticICONS[] = [
    ENTITY_ICONS.BUSINESS_FUNCTIONS.ENTITY,
    ...PROJECT_ENTITY_SUPPORTED_ICON_NAMES
];

export interface ManageProjectVersionMilestonesDialogOnCompleteReturnData {
    projectVersionMilestones: CorvalProjectVersionMilestone[],
    changes: {
        added: CorvalProjectVersionMilestone[],
        modified: CorvalProjectVersionMilestone[],
        removed: CorvalProjectVersionMilestone[],
    },
}

interface ManageProjectVersionMilestonesDialogProps extends QueryDialogProps {
    projectId: Uuid,
    projectVersionId: string,
    noopMutation?: boolean,

    onComplete?( data: ManageProjectVersionMilestonesDialogOnCompleteReturnData ): void,
}

type FormikProjectVersionMilestonesArrayItem = Pick<CorvalProjectVersionMilestone, 'id' | 'colorValue' | 'iconName' | 'name' | 'description' | 'milestoneDate' | 'milestoneId'>

interface FormValues {
    projectVersionMilestones: FormikProjectVersionMilestonesArrayItem[],
}

const ManageProjectVersionMilestonesDialogDefaultProps = {
    projectId:        null,
    projectVersionId: null,
    noopMutation:     false,
} satisfies Partial<ManageProjectVersionMilestonesDialogProps>;

function ManageProjectVersionMilestonesDialog( __props: ManageProjectVersionMilestonesDialogProps ){

    const _props = { ...ManageProjectVersionMilestonesDialogDefaultProps, ...__props };
    const {
              projectId,
              projectVersionId,
              noopMutation,
              ...props
          } = _props;

    const [ projectVersionMilestones, setProjectVersionMilestones ] = useState<CorvalProjectVersionMilestone[]>( [] ),
          [ allProjectMilestones, setAllProjectMilestones ]         = useState<Array<CorvalMilestone | CorvalProjectVersionMilestone>>( [] ),
          [ systemMilestones, setSystemMilestones ]                 = useState<CorvalMilestone[]>( [] ),
          featureFlags                                              = useFeatureFlags(),
          formikRef                                                 = useRef<FormikProps<FormValues>>( null );

    const { addIsUniqueStringTest, addStringIsDateFormatTest, addStringMinCharDescriptorTest } = useYupExtensions();


    const getProjectVersionMilestonesReq = useQuery<{ projects: CorvalProject[] }>( ProjectQueries.queries.GET_PROJECT(
        `id ${ ProjectQueries.fragments.PROJECTS.DEFINED_TIMING_FIELDS() }`
    ), {
        fetchPolicy: 'network-only',
        variables:   {
            id: projectId
        }
    } );

    const isLoading = queriesLoading( [ getProjectVersionMilestonesReq ] );

    useEffect( () => {

        if( !queriesLoading( [ getProjectVersionMilestonesReq ] ) &&
            queriesHaveData( [ getProjectVersionMilestonesReq ] ) ){

            // Get project versions milestones from the project, format records
            const project                     = _.first( getProjectVersionMilestonesReq.data.projects ),
                  projectVersionMilestones    = _.findWhere( project.projectVersions, { id: projectVersionId } ).projectVersionMilestones,
                  allProjectVersionMilestones = _.chain( project.projectVersions )
                                                 .pluck( 'projectVersionMilestones' )
                                                 .flatten()
                                                 .uniq( m => `${ m.id }-${ m.milestoneId }` )
                                                 .value();

            setProjectVersionMilestones( projectVersionMilestones );
            setAllProjectMilestones( allProjectVersionMilestones );
            setSystemMilestones( project.milestones );
        }

    }, [ getProjectVersionMilestonesReq ] );

    const getCurrentMilestoneIds = () => {

        return _.chain( formikRef?.current?.values?.projectVersionMilestones ?? [] )
                .pluck( 'milestoneId' )
                .reject( m => m == null )
                .value()
    }

    const getChanges = () => {

        const { projectVersionMilestones: formProjectVersionMilestonesValues } = formikRef.current.values;

        let added   = formProjectVersionMilestonesValues.filter( m => !_.pluck( projectVersionMilestones, 'id' ).includes( m.id ) ),
            removed = projectVersionMilestones.filter( m => !_.pluck( formProjectVersionMilestonesValues, 'id' ).includes( m.id ) );

        const modificationFieldsToCheck = [
            'colorValue',
            'iconName',
            'name',
            'description',
            'milestoneDate',
            'milestoneId',
        ]

        let modified = _.chain( projectVersionMilestones )
                        .filter( m => !_.pluck( [ ...added, ...removed ], 'id' ).includes( m.id ) )
                        .map( originalMilestone => {
                            const formMilestone = _.findWhere( formProjectVersionMilestonesValues, { id: originalMilestone.id } );

                            const originalValues = _.pick( originalMilestone, modificationFieldsToCheck )
                            const formValues = {
                                ..._.pick( formMilestone, modificationFieldsToCheck ),
                                milestoneDate: TimeService.displayDateToLocalDate( formMilestone.milestoneDate ),
                            };

                            const { diff, missing } = objectEquivalenceDiff( originalValues, formValues )

                            if( diff.length ){

                                const modifiedKeys = _.chain( diff )
                                                      .map( diffValue => _.keys( diffValue ) )
                                                      .flatten()
                                                      .value();

                                return {
                                    id: formMilestone.id,
                                    ..._.pick( formValues, modifiedKeys )
                                }
                            }

                            return null
                        } )
                        .compact()
                        .value();

        // convert display date to local date
        added = added.map( newMilestone => newMilestone?.milestoneDate
                                           ? ( { ...newMilestone, milestoneDate: TimeService.displayDateToLocalDate( newMilestone.milestoneDate ) } )
                                           : newMilestone );

        return {
            added,
            modified,
            removed,
        }
    }

    const generateGraphArgs = () => {

        if( noopMutation ) return {};

        const { added, modified, removed } = getChanges()

        return {
            variables: {
                input: {
                    projectVersionId: projectVersionId,
                    eventChanges:     {
                        added,
                        modified,
                        removed: _.pluck( removed, 'id' ),
                    }
                }
            }
        }
    };

    const handleOnComplete = ( data ) => {

        runIfFunction( props.onComplete, {
            changes:                  getChanges(),
            projectVersionMilestones: data,
        } );
    };

    // Set Formik initial values for all ids of objectives that will exist on form
    const initialValues: FormValues = {
        projectVersionMilestones: _.sortBy(
            projectVersionMilestones.map( projectVersionMilestone => ( {
                ...projectVersionMilestone,
                milestoneDate: TimeService.localDateToDisplayDate( projectVersionMilestone.milestoneDate )
            } ) ), ( item ) => TimeService.displayDateToLocalDate( item.milestoneDate )
        )
    }

    addIsUniqueStringTest();
    addStringIsDateFormatTest();
    addStringMinCharDescriptorTest()

    const validationSchema = Yup.object().shape( {
        projectVersionMilestones: Yup.array().of( Yup.object().shape( {
            id:            Yup.string(),
            colorValue:    Yup.string().nullable(),
            iconName:      Yup.string().nullable(),
            name:          Yup.string().min( 3, 'A minimum of 3 characters is required.' ).required( 'The milestone name must be specified.' ),
            description:   Yup.string().nullable().notRequired().hasDescriptorMinChar( {
                minChar:                     featureFlags.systemSettingFormFieldsDescriptorsMinCharacterLength,
                requiredWhenEnforcedMessage: 'The milestone description must be specified.',
            } ),
            milestoneDate: Yup.string().isDateFormat().required( 'A start date must be specified.' ),
            milestoneId:   Yup.string().nullable().isUniqueString( {
                // runtime method since schema builds once and doesn't update the references,
                stringSet:    getCurrentMilestoneIds,
                errorMessage: 'Base milestone must be unique within project.',
            } ),
        } ) )
    } )

    return (

        <Formik<FormValues>
            innerRef={ formikRef }
            initialValues={ initialValues }
            isInitialValid={ false }
            enableReinitialize={ true }
            validateOnChange={ true }
            validationSchema={ validationSchema }
            onSubmit={ null }>

            { formik => {

                const milestoneIds             = _.chain( allProjectMilestones ).pluck( 'milestoneId' ).reject( id => id === null ).uniq().value(),
                      thisProjectsMilestoneIds = _.chain( formik.values.projectVersionMilestones ).pluck( 'milestoneId' ).reject( id => id === null ).uniq().value(),
                      systemMilestoneIds       = _.pluck( systemMilestones, 'id' ),
                      unusedMilestoneIds       = _.difference( _.uniq( [ ...systemMilestoneIds ] ), thisProjectsMilestoneIds );

                return (

                    isLoading
                    ? (
                        <StyledContainer padding={ Size.LG }>
                            <LoadingIndicator dimmer={ false } isActive={ true } label={ 'Loading Project Version Milestones...' }/>
                        </StyledContainer>
                    ) : (
                        <QueryOpDialog
                            { ...props }
                            operationDisabled={ !projectId || !projectVersionId || isLoading || !formik.isValid }
                            operationIsHidden={ !projectId || !projectVersionId || ( projectVersionMilestones.length === 0 ) }
                            mutation={ noopMutation
                                       ? StoreQueries.mutations.EMPTY_MUTATION
                                       : ProjectQueries.mutations.UPDATE_PROJECT_VERSION_DATA() }
                            graphArgs={ generateGraphArgs }
                            onComplete={ handleOnComplete }
                            queryOptions={ {
                                onCompleted: ( data ) => {
                                    getProjectVersionMilestonesReq.refetch();
                                }
                            } }
                            submitLabel={ 'Update Milestones' }
                            className={ 'no-padding' }
                            alignControls={ Align.CENTER }>

                            <Form>

                                <BasicDialogContainer
                                    title={ 'Manage Project Version Milestones' }
                                    contentMaxHeight={ '60dvh' }
                                    contentPadding={ [ Size.MD, Size.MD, Size.LG, Size.MD ] }
                                    overflow={ true }
                                    scrolling={ true }
                                    roundedTop>

                                    <MultipleInputArrayField<FormikProjectVersionMilestonesArrayItem>
                                        name={ 'projectVersionMilestones' }
                                        label={ 'Project Version Milestones' }
                                        addLabel={ 'Add Milestone' }
                                        objectFactory={ () => ( {
                                            id:            uuid(),
                                            name:          '',
                                            description:   null,
                                            milestoneDate: TimeService.asDisplayDate( moment() ),
                                            colorValue:    ENTITY_COLORIZATION.MILESTONES.DEFAULT,
                                            iconName:      ENTITY_ICONS.MILESTONES.ENTITY,
                                        } ) }
                                        draggableRemovableContainerProps={ {
                                            marginBottom: Size.SM,
                                        } }
                                        persistFieldHeaderComponent={ true }
                                        fieldHeaderComponent={
                                            <FieldWrapper>
                                                <Grid columns={ `20px 100px 180px 1fr 175px` } gap={ '5px' }>
                                                    <Cell>
                                                        <FieldLabel>&nbsp;</FieldLabel>
                                                    </Cell>
                                                    <Cell>
                                                        <FieldLabel>Visual</FieldLabel>
                                                    </Cell>
                                                    <Cell>
                                                        <FieldLabel>Name</FieldLabel>
                                                    </Cell>
                                                    <Cell>
                                                        <FieldLabel>Description</FieldLabel>
                                                    </Cell>
                                                    <Cell>
                                                        <FieldLabel>Date</FieldLabel>
                                                    </Cell>
                                                </Grid>
                                            </FieldWrapper>
                                        }
                                        listContainerClasses={ 'ui segment raised' }
                                        renderField={ ( item, index ) => {

                                            const { iconName, colorValue } = formik.values.projectVersionMilestones[ index ],
                                                  errors                   = formik.errors.projectVersionMilestones,
                                                  menuDisplaysUpward       = ( index > ( formik.values.projectVersionMilestones.length / 2 ) );

                                            // determine if system milestone are linked, which are
                                            // used to match activities under the hood to DEM milestones,
                                            // and if there are two of the same milestoneIds being used
                                            const hasSystemMilestoneId          = systemMilestoneIds.includes( formik.values.projectVersionMilestones[ index ].milestoneId ),
                                                  hasDuplicateSystemMilestoneId = errors && errors[ index ] && errors[ index ][ 'milestoneId' ] === 'Base milestone must be unique within project.';

                                            return (

                                                <Grid columns={ `20px 100px 180px 1fr auto` }
                                                      areas={ [ 'copyMenu colorIcon name description date' ] }
                                                      gap={ '5px' }>

                                                    <Cell area={ 'copyMenu' } middle>

                                                        <AllProjectVersionMilestonesDropdown
                                                            projectId={ projectId }
                                                            projectVersionId={ projectVersionId }
                                                            includeOwnMilestones={ false }
                                                            name={ 'copyMenuForExistingProjectVersionMilestone' }>

                                                            { ( options ) => {

                                                                const hasOptions = ( _.keys( options )?.length ?? 0 ) > 0;

                                                                return (
                                                                    <UseIf x={ hasOptions }>

                                                                        <Dropdown inline
                                                                                  direction={ 'right' }
                                                                                  icon={ null }
                                                                                  closeOnEscape={ false }
                                                                                  closeOnBlur={ true }
                                                                                  openOnFocus={ true }
                                                                                  closeOnChange={ true }
                                                                                  pointing={ 'top left' }
                                                                                  scrolling={ true }
                                                                                  upward={ menuDisplaysUpward }
                                                                                  trigger={
                                                                                      <Popup
                                                                                          trigger={ <Icon name={ 'calendar' } color={ SemanticUIColor.GREY }/> }
                                                                                          content={ 'Copy values from existing milestones from other project versions' }
                                                                                          size={ 'small' }
                                                                                      />
                                                                                  }>

                                                                            <Dropdown.Menu style={ { maxHeight: 185 } }>

                                                                                { _.chain( options )
                                                                                   .groupBy( 'group' )
                                                                                   .pairs()
                                                                                   .value()
                                                                                   .map( ( [ groupName, group ] ) => {

                                                                                       return (
                                                                                           <>

                                                                                               <Menu.Header style={ { width: 400 } }>{ groupName }</Menu.Header>
                                                                                               { group.map( ( value ) => {

                                                                                                   const { id, name, iconName, colorValue, description, milestoneId } = value;

                                                                                                   const hasEmptyDescription = _.isEmpty( trimString( description ) );

                                                                                                   return (
                                                                                                       <MenuItemWithIconAndIndicator
                                                                                                           key={ id }
                                                                                                           menuIcon={ ENTITY_ICONS.MILESTONES.ITEM }
                                                                                                           iconProps={ {
                                                                                                               className: 'rotate--45',
                                                                                                               color:     colorValue,
                                                                                                               iconName:  iconName,
                                                                                                           } }
                                                                                                           withIndicator={ false }
                                                                                                           onClick={ () => {

                                                                                                               const base = formik.values.projectVersionMilestones[ index ];

                                                                                                               formik.setFieldValue( `projectVersionMilestones[${ index }]`, {
                                                                                                                       id:            base.id,
                                                                                                                       milestoneDate: base.milestoneDate,
                                                                                                                       name,
                                                                                                                       description,
                                                                                                                       milestoneId,
                                                                                                                       colorValue,
                                                                                                                       iconName,
                                                                                                                   }
                                                                                                               )
                                                                                                           } }>

                                                                                                           <Grid columns={ `1fr` } rowGap={ '0px' }>
                                                                                                               <Cell>{ name }</Cell>
                                                                                                               <Cell><FinePrintMessage className={ 'text--wrap' }>{ hasEmptyDescription ? 'No description' : description }</FinePrintMessage></Cell>
                                                                                                           </Grid>

                                                                                                       </MenuItemWithIconAndIndicator>
                                                                                                   )
                                                                                               } ) }

                                                                                           </>
                                                                                       )
                                                                                   } ) }

                                                                            </Dropdown.Menu>

                                                                        </Dropdown>
                                                                    </UseIf>
                                                                )
                                                            } }

                                                        </AllProjectVersionMilestonesDropdown>

                                                    </Cell>
                                                    <Cell area={ 'colorIcon' } middle>

                                                        <Grid columns={ `40px 60px` } gap={ '5px' }>

                                                            <Cell middle>
                                                                <ColorMenu
                                                                    gridSize={ 6 }
                                                                    cellWidth={ 30 }
                                                                    triggerDotSize={ 17 }
                                                                    sampleDotSize={ 24 }
                                                                    colorOptions={ supportedProjectVersionMilestoneColorNames }
                                                                    dropdownProps={ {
                                                                        direction:     "right",
                                                                        closeOnEscape: true,
                                                                        closeOnBlur:   true,
                                                                        upward:        menuDisplaysUpward,
                                                                    } }
                                                                    triggerButtonProps={ {
                                                                        style: { width: 40 }
                                                                    } }
                                                                    value={ colorValue as CorvalColor }
                                                                    onClick={ value => formik.setFieldValue( `projectVersionMilestones[${ index }].colorValue`, value ) }
                                                                />

                                                            </Cell>
                                                            <Cell middle>

                                                                <IconMenu
                                                                    gridSize={ 8 }
                                                                    cellWidth={ 50 }
                                                                    iconOptions={ supportedProjectVersionMilestoneIconNames }
                                                                    dropdownProps={ {
                                                                        direction:     "right",
                                                                        closeOnEscape: true,
                                                                        closeOnBlur:   true,
                                                                        upward:        menuDisplaysUpward,
                                                                    } }
                                                                    triggerButtonProps={ {
                                                                        style: { width: `calc(100% - 5px)` }
                                                                    } }
                                                                    activeColor={ CorvalUIColor.LIGHTBLACK }
                                                                    valueColor={ colorValue as CorvalColor }
                                                                    value={ iconName as SemanticICONS }
                                                                    onClick={ value => formik.setFieldValue( `projectVersionMilestones[${ index }].iconName`, value ) }
                                                                />

                                                            </Cell>

                                                        </Grid>


                                                    </Cell>
                                                    <Cell area={ 'name' } middle className={ 'position--relative' }>

                                                        <div>
                                                            <TextField
                                                                size={ 'small' }
                                                                name={ `projectVersionMilestones[${ index }].name` }
                                                                style={ { marginBottom: 0 } }
                                                            />
                                                        </div>

                                                        {/* System Milestone Linkage */ }
                                                        { hasSystemMilestoneId && (
                                                            <StyledContainer position={ Position.ABSOLUTE } top={ -7 } right={ -10 }>
                                                                <Tooltip
                                                                    iconName={ 'linkify' }
                                                                    color={ hasDuplicateSystemMilestoneId ? SemanticUIColor.RED : SemanticUIColor.PURPLE }
                                                                    popupProps={ {
                                                                        closeOnTriggerMouseLeave: false,
                                                                        closeOnPortalMouseLeave:  true,
                                                                    } }
                                                                    content={
                                                                        <FinePrintMessage>
                                                                            { hasDuplicateSystemMilestoneId ? <><strong className={ 'text--colored-danger' }>Duplicate Link.</strong><br/></> : '' }
                                                                            Based on the system milestone
                                                                            <br/>
                                                                            <strong>{ _.findWhere( systemMilestones, { id: formik.values.projectVersionMilestones[ index ].milestoneId } )?.name }</strong>.
                                                                            <br/>
                                                                            <TextLink onClick={ () => formik.setFieldValue( `projectVersionMilestones[${ index }].milestoneId`, null ) }>Remove Link</TextLink>
                                                                        </FinePrintMessage>
                                                                    }/>
                                                            </StyledContainer>
                                                        ) }

                                                    </Cell>
                                                    <Cell area={ 'description' } middle>

                                                        <TextField
                                                            size={ 'small' }
                                                            name={ `projectVersionMilestones[${ index }].description` }
                                                            debounce={ true }
                                                            debounceValue={ formik.values.projectVersionMilestones[ index ].description }
                                                            onDebounceChange={ ( value ) => {
                                                                formik.setFieldValue( `projectVersionMilestones[${ index }].description`, value );
                                                            } }/>

                                                    </Cell>
                                                    <Cell area={ 'date' } middle>

                                                        <DateField
                                                            name={ `projectVersionMilestones[${ index }].milestoneDate` }
                                                            size={ 'small' }
                                                            clearable={ false }
                                                            style={ { maxWidth: 150 } }
                                                            disabled={ isLoading }
                                                            onAfterChange={ ( value ) => {} }
                                                            initialDate={ moment() }
                                                            popupPosition={ 'bottom right' }
                                                            includeErrorMessage={ false }
                                                        />

                                                    </Cell>

                                                </Grid>

                                            )
                                        } }/>

                                </BasicDialogContainer>
                            </Form>

                        </QueryOpDialog>
                    )
                )
            } }

        </Formik>
    );
}

export {
    ManageProjectVersionMilestonesDialog,
    ManageProjectVersionMilestonesDialogDefaultProps,
    type ManageProjectVersionMilestonesDialogProps,
};
